import { Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '@/lib/auth';
import {
    LayoutDashboard,
    Newspaper,
    KanbanSquare,
    Users,
    UserCheck,
    GraduationCap,
    LogOut,
    Settings,
    Building2,
    User,
    ChevronDown,
    ListTodo,
    Calendar,
    LifeBuoy,
    Search,
    X,
    Sparkles,
    Wrench,
    UserPlus,
    Lightbulb,
    FileText,
    ExternalLink,
    Gauge
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { GlobalSearch } from '@/components/common/GlobalSearch';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import {
    Sheet,
    SheetContent,
    SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const ROLE_NAMES = {
    admin_full: 'Admin Full',
    admin: 'Admin',
    comptable: 'Comptable',
    vp: 'VP',
    apporteur: 'Apporteur'
};

const allMenuItems = [
    { icon: Newspaper, label: "Fil d'actualité", path: '/feed', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
    { icon: Sparkles, label: 'Inside VNX', path: '/inside-vnx', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
    { icon: KanbanSquare, label: 'Pipeline', path: '/pipeline', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
    { icon: Users, label: 'Prospects', path: '/prospects', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
    { icon: UserCheck, label: 'Clients', path: '/clients', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
    { icon: ListTodo, label: 'Tâches', path: '/tasks', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
    { icon: Gauge, label: 'Implication', path: '/implication', roles: ['admin_full', 'admin', 'vp'] },
    { icon: Calendar, label: 'Calendrier', path: '/calendar', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
    { icon: Building2, label: 'Équipes', path: '/teams', roles: ['admin_full', 'admin', 'vp'] },
    { icon: UserPlus, label: 'Onboarding', path: '/onboarding', roles: ['admin_full', 'admin', 'vp'] },
    { icon: GraduationCap, label: 'Académie', path: '/academy', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
    { icon: Lightbulb, label: 'Boîte à idées', path: '/boite-a-idees', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'], isNew: true },
    { icon: Wrench, label: 'Outils & liens utiles', path: '/outils', roles: ['admin_full', 'admin', 'comptable', 'vp', 'apporteur'] },
    { icon: LifeBuoy, label: 'Support', path: '/support', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
    { icon: Settings, label: 'Paramètres', path: '/settings', roles: ['admin_full', 'admin'] },
    { icon: FileText, label: 'Candidatures', href: 'https://valnexo.fr/admin/candidatures', external: true, roles: ['admin_full', 'admin'], isNew: true },
];

// Pastille « Nouveau » animée (partagée desktop/mobile via localStorage)
const SEEN_NAV_KEY = 'vnx_seen_nav_v1';
const NewBadge = () => (
    <span
        className="inline-flex items-center gap-1 rounded-full bg-yellow-500/20 px-2 py-0.5 text-[10px] font-semibold text-yellow-400 ring-1 ring-yellow-500/40"
        data-testid="nav-new-badge-mobile"
    >
        <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-yellow-400" />
        </span>
        Nouveau
    </span>
);

// Bottom nav items - limited space, show most used
const mobileBottomNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: KanbanSquare, label: 'Pipeline', path: '/pipeline' },
    { icon: Users, label: 'Prospects', path: '/prospects' },
    { icon: UserCheck, label: 'Clients', path: '/clients' },
    { icon: Settings, label: 'Plus', path: null, isMore: true }, // Opens sheet menu
];

// Composant Avatar utilisateur avec fallback
const UserAvatar = ({ user, size = 'md' }) => {
    const sizeClasses = {
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base'
    };

    const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
    
    // Supporte les deux formats : avatar_url et profile_picture
    const avatarUrl = user?.avatar_url || user?.profile_picture;

    if (avatarUrl) {
        return (
            <img 
                src={avatarUrl} 
                alt={`${user?.first_name} ${user?.last_name}`}
                className={cn(
                    "rounded-full object-cover ring-2 ring-white/20",
                    sizeClasses[size]
                )}
            />
        );
    }

    if (initials) {
        return (
            <div className={cn(
                "rounded-full bg-gradient-to-br from-yellow-600 to-yellow-500 flex items-center justify-center font-semibold text-white ring-2 ring-white/20",
                sizeClasses[size]
            )}>
                {initials}
            </div>
        );
    }

    return (
        <div className={cn(
            "rounded-full bg-slate-600 flex items-center justify-center ring-2 ring-white/20",
            sizeClasses[size]
        )}>
            <User className="w-1/2 h-1/2 text-slate-300" />
        </div>
    );
};

// Composant UserMenuTrigger - Déclencheur du menu utilisateur
const UserMenuTrigger = ({ user, onClick, className }) => {
    return (
        <button
            onClick={onClick}
            className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all",
                "hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-500/50",
                "cursor-pointer select-none",
                className
            )}
            aria-label="Ouvrir le menu utilisateur"
            data-testid="user-menu-trigger"
        >
            <UserAvatar user={user} size="sm" />
            
            {/* Desktop: Prénom NOM */}
            <span className="hidden lg:block text-sm font-medium text-white max-w-[150px] truncate">
                {user?.first_name} {user?.last_name?.toUpperCase()}
            </span>
            
            {/* Tablet: Prénom seul */}
            <span className="hidden sm:block lg:hidden text-sm font-medium text-white">
                {user?.first_name}
            </span>
            
            {/* Mobile (<480px): Avatar seul - pas de texte */}
            
            <ChevronDown className="hidden sm:block w-4 h-4 text-slate-400" />
        </button>
    );
};

export const MobileNav = () => {
    const location = useLocation();
    const { user, logout, isTrainingRestricted, unreadFeedCount, wallUnreadCount } = useAuth();
    const [sheetOpen, setSheetOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [seenNav, setSeenNav] = useState(() => {
        try { return JSON.parse(localStorage.getItem(SEEN_NAV_KEY) || '[]'); } catch { return []; }
    });

    const markNavSeen = (id) => {
        setSeenNav((prev) => {
            if (prev.includes(id)) return prev;
            const next = [...prev, id];
            try { localStorage.setItem(SEEN_NAV_KEY, JSON.stringify(next)); } catch { /* noop */ }
            return next;
        });
    };

    const handleLogout = () => {
        setSheetOpen(false);
        setDropdownOpen(false);
        logout();
    };

    const restricted = isTrainingRestricted();
    const userHasAdminAccess = user?.is_admin === true;
    const visibleMenuItems = allMenuItems.filter(item =>
        (item.roles.includes(user?.role)
            || (userHasAdminAccess && (item.roles.includes('admin_full') || item.roles.includes('admin')))) &&
        (!restricted || ['/academy', '/support'].includes(item.path))
    );

    const bottomNavItems = restricted
        ? [
            { icon: GraduationCap, label: 'Académie', path: '/academy' },
            { icon: LifeBuoy, label: 'Support', path: '/support' },
            { icon: Settings, label: 'Plus', path: null, isMore: true },
          ]
        : mobileBottomNavItems;

    // Fermer le dropdown avec Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    return (
        <>
            {/* Top bar - visible sur mobile et tablet, avec menu utilisateur */}
            <header 
                className="md:hidden fixed top-0 left-0 right-0 bg-slate-900 border-b border-white/10 flex items-center justify-between px-4 z-50"
                style={{ paddingTop: 'env(safe-area-inset-top, 0px)', height: 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}
            >
                {/* Logo */}
                <Link to="/dashboard" className="flex items-center gap-2">
                    <img 
                        src="/valnexo-logo-official-new.png"
                        alt="Valnexo Logo"
                        className="w-7 h-7 object-contain rounded"
                    />
                    <span className="font-bold text-base text-white">Valnexo</span>
                </Link>
                
                {/* User Menu Trigger - remplace le hamburger */}
                <div className="flex items-center gap-1">
                    {/* Bouton recherche -> ouvre l'overlay plein écran */}
                    <button
                        onClick={() => setSearchOpen(true)}
                        className="p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                        aria-label="Rechercher"
                        data-testid="mobile-search-trigger"
                    >
                        <Search className="w-5 h-5 text-white" />
                    </button>
                    {/* Cloche notifications (entre la loupe et la photo de profil) */}
                    <div data-testid="mobile-notification-bell">
                        <NotificationCenter />
                    </div>
                    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetTrigger asChild>
                        <div>
                            <UserMenuTrigger user={user} onClick={() => setSheetOpen(true)} />
                        </div>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-80 p-0 bg-slate-900 border-l border-white/10">
                        <div className="flex flex-col h-full">
                            {/* Header avec infos utilisateur */}
                            <div className="p-4 border-b border-white/10">
                                <div className="flex items-center gap-3">
                                    <UserAvatar user={user} size="lg" />
                                    <div className="flex-1">
                                        <p className="text-white font-medium">
                                            {user?.first_name} {user?.last_name?.toUpperCase()}
                                        </p>
                                        <Badge className="mt-1 bg-yellow-600/20 text-yellow-400 text-xs border-0">
                                            {ROLE_NAMES[user?.role] || user?.role}
                                        </Badge>
                                    </div>
                                </div>
                            </div>

                            {/* Menu Items */}
                            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                                {visibleMenuItems.map((item) => {
                                    const isActive = item.path && location.pathname === item.path;
                                    const showFeedBadge = item.path === '/feed' && unreadFeedCount > 0;
                                    const showWallBadge = item.path === '/prospects' && wallUnreadCount > 0;
                                    const badgeCount = showFeedBadge ? unreadFeedCount : (showWallBadge ? wallUnreadCount : 0);
                                    const navId = item.path || item.href;
                                    const showNew = item.isNew && !seenNav.includes(navId);

                                    if (item.external) {
                                        return (
                                            <a
                                                key={item.href}
                                                href={item.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={() => { markNavSeen(navId); setSheetOpen(false); }}
                                                data-testid="mobile-candidatures-link"
                                                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-slate-300 hover:bg-white/10 hover:text-white"
                                            >
                                                <item.icon className="w-5 h-5" />
                                                <span className="font-medium flex-1">{item.label}</span>
                                                {showNew ? <NewBadge /> : <ExternalLink className="w-4 h-4 text-slate-500" />}
                                            </a>
                                        );
                                    }

                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            reloadDocument={item.path === '/inside-vnx'}
                                            onClick={() => { if (showNew) markNavSeen(navId); setSheetOpen(false); }}
                                            className={cn(
                                                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                                                isActive 
                                                    ? "bg-yellow-600 text-white" 
                                                    : "text-slate-300 hover:bg-white/10 hover:text-white"
                                            )}
                                        >
                                            <item.icon className="w-5 h-5" />
                                            <span className="font-medium flex-1">{item.label}</span>
                                            {(showFeedBadge || showWallBadge) && (
                                                <span
                                                    className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold leading-none"
                                                    data-testid={`${showWallBadge ? 'wall' : 'feed'}-unread-badge-mobile`}
                                                >
                                                    {badgeCount > 9 ? '9+' : badgeCount}
                                                </span>
                                            )}
                                            {showNew && !(showFeedBadge || showWallBadge) && <NewBadge />}
                                        </Link>
                                    );
                                })}
                            </nav>

                            {/* Logout Button */}
                            <div className="p-4 border-t border-white/10">
                                <Button
                                    onClick={handleLogout}
                                    variant="destructive"
                                    className="w-full bg-red-600 hover:bg-red-700"
                                    data-testid="mobile-logout-btn"
                                >
                                    <LogOut className="w-5 h-5 mr-2" />
                                    Déconnexion
                                </Button>
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
                </div>
            </header>

            {/* Overlay de recherche plein écran (mobile) */}
            {searchOpen && (
                <div
                    className="md:hidden fixed inset-0 z-[60] bg-white flex flex-col"
                    data-testid="mobile-search-overlay"
                >
                    <div
                        className="flex items-center gap-2 px-4 border-b border-slate-200 bg-white"
                        style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top, 0px))', paddingBottom: '0.5rem' }}
                    >
                        <div className="flex-1">
                            <GlobalSearch
                                variant="mobile"
                                showCallButton
                                autoFocus
                                onResultNavigate={() => setSearchOpen(false)}
                            />
                        </div>
                        <button
                            onClick={() => setSearchOpen(false)}
                            className="p-2 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
                            aria-label="Fermer la recherche"
                            data-testid="mobile-search-close"
                        >
                            <X className="w-5 h-5 text-slate-600" />
                        </button>
                    </div>
                </div>
            )}

            {/* Bottom navigation for mobile — rendu via PORTAIL sur document.body pour
                échapper à tout ancêtre (#root overflow-clip, transform injecté, etc.) qui
                casse le position:fixed sur iOS Safari (la barre « remontait au scroll »). */}
            {createPortal(
            <nav 
                className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex justify-around items-center z-50"
                style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', height: 'calc(4rem + env(safe-area-inset-bottom, 0px))' }}
                data-testid="mobile-bottom-nav"
            >
                {bottomNavItems.map((item, index) => {
                    // "Plus" button opens the sheet menu
                    if (item.isMore) {
                        return (
                            <button
                                key="more"
                                onClick={() => setSheetOpen(true)}
                                className="flex flex-col items-center gap-1 py-2 px-3"
                                data-testid="mobile-more-btn"
                            >
                                <item.icon className="w-5 h-5 text-slate-400" />
                                <span className="text-xs font-medium text-slate-500">
                                    {item.label}
                                </span>
                            </button>
                        );
                    }
                    
                    const isActive = location.pathname === item.path;
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            reloadDocument={item.path === '/inside-vnx'}
                            className="flex flex-col items-center gap-1 py-2 px-3"
                            data-testid={`mobile-nav-${item.path?.replace('/', '')}`}
                        >
                            <item.icon className={cn(
                                "w-5 h-5 transition-colors",
                                isActive ? "text-yellow-600" : "text-slate-400"
                            )} />
                            <span className={cn(
                                "text-xs font-medium transition-colors",
                                isActive ? "text-yellow-600" : "text-slate-500"
                            )}>
                                {item.label}
                            </span>
                        </Link>
                    );
                })}
            </nav>,
            document.body
            )}
        </>
    );
};

// Export UserAvatar et UserMenuTrigger pour réutilisation
export { UserAvatar, UserMenuTrigger, ROLE_NAMES, allMenuItems };
